import React from 'react';
import { useParams } from 'react-router-dom';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useModal } from '@ebay/nice-modal-react';
import { Button, Col, notification, Row, Skeleton } from 'antd';
import moment from 'moment';

import {
  ITransferRequest,
  TransferRequestStatus,
  useTransferRequest
} from 'api/bdo/transfer-requests';
import { DetailRow, WrapperPanel } from 'components';
import BDOTransferRequestAuditLog from 'components/AuditLog/BDOTransferRequestAuditLog';
import { PageContent, PageHeader } from 'components/page';
import { getCardTypeByName } from 'containers/BDO/utils';
import { usePermissions } from 'hooks';
import { formatNumber } from 'utils/formatters';
import API from 'utils/request';

import { dateFormat, dateTimeFormat } from '../../constants';
import RequestStatusTag from '../components/RequestStatusTag';
import { StyledButton } from '../components/StyledButton';
import UpdateRequestStatusModal from '../components/UpdateRequestStatusModal';
import { TransferRequestAccountTypes, TransferRequestTypeLabels } from '../utils';

const Placeholder = () => (
  <Skeleton title={false} paragraph={{ rows: 1, width: 120 }} style={{ marginBottom: '-1em' }} />
);

const DetailRowWithLoading = ({ data, label, loading }) => (
  <DetailRow label={label} data={loading ? <Placeholder /> : data} />
);

const BDOTransferRequest = () => {
  const { id: requestId } = useParams<{ id: string }>();
  const updateStatusModal = useModal(UpdateRequestStatusModal);

  const { data, isLoading, mutate } = useTransferRequest(requestId);

  const { canEdit } = usePermissions('points_redemptions');

  const disabledAction = !canEdit || isLoading;

  const setToInProgress = () => {
    API.patch(`/v4/dash/custom/bdo/point_transfers/${requestId}`, {
      state: 'in_progress',
      reason: ''
    })
      .then(() => {
        notification.success({ message: 'Status changed to ‘In progress’' });
        mutate();
      })
      .catch(err => {
        notification.error({ message: 'Error happened', description: err?.message });
      });
  };
  const completeRequest = () => {
    updateStatus('completed');
  };
  const declineRequest = () => {
    updateStatus('declined');
  };
  const cancelRequest = () => {
    updateStatus('cancelled');
  };

  const updateStatus = (state: TransferRequestStatus) => {
    updateStatusModal
      .show({ request: data, state })
      .then((data: ITransferRequest | null) => {
        let message = '';
        let description = '';

        switch (state) {
          case 'completed':
            message = 'Approval successful';
            description = 'The transfer request has been approved.';
            break;
          case 'declined':
            message = data?.properties?.senders_stored_value_transaction_id
              ? 'Decline successful'
              : 'Request Declined Without Refund';
            description = data?.properties?.senders_stored_value_transaction_id
              ? 'The transfer request has been declined.'
              : 'The request was successfully declined, but there was no transaction to refund. You do not need to take any further action for this request.';
            break;
          case 'cancelled':
            message = 'Cancel successful';
            description = 'The transfer request has been cancelled.';
            break;
        }

        // The state change always commits (HTTP 200); ccm_result, when present,
        // reports whether the BRC member's SMS notification went out.
        const ccm = data?.ccm_result;

        if (ccm?.success === false) {
          notification.open({
            icon: <ExclamationCircleOutlined style={{ color: '#ff8c00' }} />,
            message,
            description: `${description} SMS failed to send because ${
              ccm.message || 'an unknown error'
            }`
          });
        } else if (ccm?.success === true) {
          notification.success({
            message,
            description: `${description} An SMS message sent informing the BRC member.`
          });
        } else {
          notification.success({ message, description });
        }
      })
      .catch(err => {
        let message = '';
        let description = err?.message;

        switch (state) {
          case 'completed':
            message = 'The request could not be approved';
            break;
          case 'declined':
            message = 'The request could not be declined';
            break;
          case 'cancelled':
            message = 'The request could not be cancelled';
            break;
        }

        notification.error({ message, description });
      })
      .finally(mutate);
  };

  const cardName = () => {
    if (data?.request_type === 'hotel_loyalty') {
      return data?.properties?.hotel_loyalty_name;
    } else if (data?.request_type === 'membership_fee' || data?.request_type === 'cash_credit') {
      return data?.properties?.card_item_name;
    } else if (data?.request_type === 'evoucher' || data?.request_type === 'paper') {
      return data?.properties?.voucher_name;
    } else if (data?.request_type === 'airmile') {
      return data?.properties?.airmile_name;
    }
  };

  const typeLabel = TransferRequestTypeLabels[data?.request_type];
  const [senderType, receiverType] = TransferRequestAccountTypes[data?.request_type] || [];

  let toField = [data?.properties?.receiver_first_name, data?.properties?.receiver_last_name]
    .filter(Boolean)
    .join(' ');

  if (data?.request_type === 'evoucher' || data?.request_type === 'paper') {
    toField = `${cardName()} x ${data?.quantity?.toString()}`;
  }

  let toReceiverField;

  const suffix = data?.properties?.receiver_suffix || '';

  switch (data?.request_type) {
    case 'airmile':
    case 'hotel_loyalty':
      toReceiverField = `${data?.properties?.account_number}`;
      break;
    case 'evoucher':
    case 'paper':
      toReceiverField = null;
      break;
    case 'cash_credit':
      toReceiverField = `${cardName()} ${suffix}`;
      break;
    default:
      toReceiverField = `${data?.properties?.receiver} ${suffix}`;
  }

  return (
    <>
      <PageHeader title="Request details" data-testid="page-header" />
      <PageContent>
        <WrapperPanel title="Request details">
          <DetailRowWithLoading
            loading={isLoading}
            label="Status"
            data={<RequestStatusTag state={data?.state} />}
          />
          <DetailRowWithLoading
            loading={isLoading}
            label="Created on"
            data={moment(data?.created_at).format(dateTimeFormat)}
          />
          {['airmile', 'hotel_loyalty', 'paper', 'evoucher', 'cash_credit'].includes(
            data?.request_type
          ) &&
            !['membership_fee'].includes(data?.request_type) && (
              <DetailRowWithLoading
                loading={isLoading}
                label="Redemption Type"
                data={getCardTypeByName(data?.request_type)}
              />
            )}
          <DetailRowWithLoading loading={isLoading} label="Request type" data={typeLabel} />
          <DetailRowWithLoading
            loading={isLoading}
            label="From"
            data={
              <Row style={{ width: '100%', maxWidth: 1024 }}>
                <Col sm={12}>
                  {[data?.properties?.sender_first_name, data?.properties?.sender_last_name]
                    .filter(Boolean)
                    .join(' ')}
                </Col>
                <Col sm={12}>
                  {data?.card_type === 'amex' || data?.card_type === 'diners' ? (
                    data?.properties?.sender
                  ) : (
                    <>
                      {senderType}: {data?.properties?.sender}
                    </>
                  )}
                </Col>
              </Row>
            }
          />
          <DetailRowWithLoading
            loading={isLoading}
            label="Card type"
            data={
              <Row style={{ width: '100%', maxWidth: 1024 }}>
                <Col sm={12}>{data?.properties?.card_name}</Col>
              </Row>
            }
          />
          {(data?.card_type === 'amex' || data?.card_type === 'diners') &&
            !['evoucher', 'paper', 'cash_credit'].includes(data?.request_type) && (
              <DetailRowWithLoading
                loading={isLoading}
                label="Destination"
                data={
                  <Row style={{ width: '100%', maxWidth: 1024 }}>
                    <Col sm={12}>{cardName()}</Col>
                    {data?.request_type === 'membership_fee' && (
                      <>
                        {data?.supplementary ? (
                          <Col sm={12}>Supplementary</Col>
                        ) : (
                          <Col sm={12}>Principal</Col>
                        )}
                      </>
                    )}
                  </Row>
                }
              />
            )}
          <DetailRowWithLoading
            loading={isLoading}
            label="To"
            data={
              <Row style={{ width: '100%', maxWidth: 1024 }}>
                {data?.request_type === 'CC-HOPE' ? (
                  <>HOPE Donation</>
                ) : (
                  <>
                    <Col sm={12}>
                      {toField}{' '}
                      {['airmile', 'hotel_loyalty'].includes(data?.request_type) ? suffix : null}
                    </Col>
                    <Col sm={12}>
                      {data?.card_type === 'amex' || data?.card_type === 'diners' ? (
                        toReceiverField
                      ) : (
                        <>
                          {receiverType}: {toReceiverField}
                        </>
                      )}
                    </Col>
                  </>
                )}
              </Row>
            }
          />
          {data?.request_type === 'membership_fee' && (
            <DetailRowWithLoading
              loading={isLoading}
              label="Statement Date"
              data={moment(data?.statement_date).format(dateFormat)}
            />
          )}
          <DetailRowWithLoading
            loading={isLoading}
            label="LRS Code"
            data={
              <Row style={{ width: '100%', maxWidth: 1024 }}>
                <Col sm={12}>{data?.properties?.lrsRewardCode}</Col>
              </Row>
            }
          />
          {['airmile', 'hotel_loyalty'].includes(data?.request_type) && (
            <DetailRowWithLoading
              loading={isLoading}
              label="Conversion Rate"
              data={getCardTypeByName(data?.properties?.conversion_rate)}
            />
          )}
          <DetailRowWithLoading
            loading={isLoading}
            label={
              data?.card_type === 'amex' || data?.card_type === 'diners'
                ? 'Redeem Amount'
                : 'Amount'
            }
            data={
              <Row style={{ width: '100%', maxWidth: 1024 }}>
                <Col sm={12}>{formatNumber(data?.amount / 100)}</Col>
              </Row>
            }
          />
          {data?.request_type !== 'evoucher' &&
          data?.request_type !== 'paper' &&
          data?.request_type !== 'membership_fee' ? (
            <DetailRowWithLoading
              loading={isLoading}
              label={data?.request_type === 'cash_credit' ? 'Cash Credit' : 'Points to transfer'}
              data={
                data?.card_type === 'amex' || data?.card_type === 'diners'
                  ? data?.request_type === 'cash_credit'
                    ? `${data?.properties?.currency} ${data?.to_receive / 100}`
                    : formatNumber(data?.to_receive / 100)
                  : formatNumber(data?.to_receive / 100)
              }
            />
          ) : null}
          {data?.state !== 'new' && (
            <DetailRowWithLoading
              loading={isLoading}
              label="Last updated by"
              data={
                <Row style={{ width: '100%', maxWidth: 1024 }}>
                  <Col sm={24} md={8}>
                    {[data?.properties?.updated_by].filter(Boolean).join(' ')}
                  </Col>
                  <Col sm={24} md={8}>
                    {moment(data?.updated_at).format(dateTimeFormat)}
                  </Col>
                </Row>
              }
            />
          )}

          {['completed', 'declined', 'cancelled'].includes(data?.state) && (
            <DetailRowWithLoading loading={isLoading} label="Reason" data={data?.reason} />
          )}

          {data?.state === 'new' && (
            <Col xs={24} md={{ offset: 8, span: 16 }} style={{ display: 'flex', gap: 16 }}>
              <Button
                type="primary"
                size="large"
                disabled={disabledAction}
                onClick={setToInProgress}
                ghost
              >
                Change status to ‘In progress’
              </Button>
            </Col>
          )}
          {data?.state === 'in_progress' && (
            <Col xs={24} md={{ offset: 8, span: 16 }} style={{ display: 'flex', gap: 16 }}>
              <StyledButton
                type="primary"
                size="large"
                disabled={disabledAction}
                onClick={completeRequest}
                state="completed"
                style={{ width: 120 }}
              >
                Complete
              </StyledButton>
              <StyledButton
                type="primary"
                size="large"
                disabled={disabledAction}
                onClick={declineRequest}
                state="declined"
                style={{ width: 120 }}
              >
                Decline
              </StyledButton>
              <StyledButton
                type="primary"
                size="large"
                disabled={disabledAction}
                onClick={cancelRequest}
                state="cancelled"
                style={{ width: 120 }}
              >
                Cancel
              </StyledButton>
            </Col>
          )}
        </WrapperPanel>

        <BDOTransferRequestAuditLog id={requestId} state={data?.state} />
      </PageContent>
    </>
  );
};

export default BDOTransferRequest;
